import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import '@/themes.css';

const ThemePreview = () => {
  const navigate = useNavigate();
  const logoUrl = '/assets/hero/logo.png';

  const themes = [
    {
      name: 'Warm Browns with Terracotta',
      class: 'theme-warm',
      description: 'Warm, inviting browns with vibrant terracotta orange accents',
      gradient: 'from-orange-100 via-amber-50 to-yellow-50'
    },
    {
      name: 'Rich Chocolate with Sage Green',
      class: 'theme-rich',
      description: 'Deep, rich coffee browns with calming sage green highlights',
      gradient: 'from-stone-100 via-neutral-50 to-amber-50'
    },
    {
      name: 'Earthy Tan with Mustard Gold',
      class: 'theme-earthy',
      description: 'Natural earth tones with bold mustard gold accents',
      gradient: 'from-yellow-50 via-stone-50 to-amber-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Theme Options Preview</h1>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Site
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-center text-gray-600 mb-12 text-lg">
          Choose your preferred color theme. Each option features brown/tan base colors with unique accent colors.
        </p>

        <div className="space-y-16">
          {themes.map((theme, index) => (
            <div key={theme.class} className="space-y-6">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Option {index + 1}: {theme.name}
                </h2>
                <p className="text-gray-600">{theme.description}</p>
              </div>

              {/* Theme Demo */}
              <div className={`${theme.class} themed rounded-2xl overflow-hidden shadow-2xl`}>
                {/* Navbar */}
                <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200">
                  <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img src={logoUrl} alt="Huemend Logo" className="h-12 w-12 object-contain" />
                      <span className="text-2xl font-bold" style={{color: 'var(--primary)'}}>Huemend</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <button className="text-gray-700 hover:text-gray-900 px-3 py-2">Browse Healers</button>
                      <button className="text-gray-700 hover:text-gray-900 px-3 py-2">Join As A Healer</button>
                      <button className="btn-primary px-6 py-2 rounded-lg font-medium">Sign In</button>
                    </div>
                  </div>
                </nav>

                {/* Hero Section */}
                <section className={`py-20 px-6 bg-gradient-to-br ${theme.gradient}`}>
                  <div className="max-w-4xl mx-auto text-center space-y-6">
                    <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                      Find Your Path to
                      <span className="block text-primary mt-2">Holistic Healing</span>
                    </h1>
                    <p className="text-lg text-gray-700">
                      Connect with experienced healers of color - from doctors and therapists to reiki practitioners and shamans.
                    </p>
                    
                    <div className="flex gap-3 max-w-2xl mx-auto mt-8">
                      <input
                        type="text"
                        placeholder="Search by healer type, specialty, or location..."
                        className="flex-1 px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none"
                      />
                      <button className="accent-btn px-8 py-4 rounded-xl font-medium">Search</button>
                    </div>
                  </div>
                </section>

                {/* Features */}
                <section className="py-16 px-6">
                  <div className="max-w-6xl mx-auto">
                    <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Why Choose Huemend</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                      {['Diverse Practitioners', 'Verified Healers', 'Easy Booking'].map((title, i) => (
                        <div key={i} className="glass-card p-8 hover:shadow-xl transition-shadow">
                          <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                               style={{background: 'var(--accent)'}}>
                            <span className="text-2xl text-white">✓</span>
                          </div>
                          <h3 className="text-xl font-bold mb-3 text-gray-900">{title}</h3>
                          <p className="text-gray-600">
                            Quality care from experienced professionals in your community.
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* CTA */}
                <section className="py-16 px-6" style={{background: 'var(--primary)'}}>
                  <div className="max-w-4xl mx-auto text-center space-y-6 text-white">
                    <h2 className="text-4xl font-bold">Ready to Begin Your Healing Journey?</h2>
                    <p className="text-xl opacity-90">
                      Join thousands finding wellness through culturally connected care.
                    </p>
                    <button className="bg-white px-10 py-4 rounded-xl text-lg font-semibold mt-8"
                            style={{color: 'var(--primary)'}}>
                      Explore Healers
                    </button>
                  </div>
                </section>
              </div>
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div className="mt-16 p-8 bg-blue-50 rounded-2xl border border-blue-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">How to Choose</h3>
          <p className="text-gray-700 leading-relaxed">
            Review all three options above. Each shows your logo and demonstrates the color palette in action.
            Let me know which option you prefer (Option 1, 2, or 3), and I'll apply it to your entire site!
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThemePreview;
